import { useEffect, useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import WCButton from "@/components/WCButton";

// Statement-driven hero — no imagery, just brutal typography and negative space.
// Reveals line-by-line so each statement lands before the next appears.
// Use for conceptual drops (System) where the idea itself IS the image.
export default function ManifestoHero({ lines, sublabel, ctaHref, redWord }) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 250);
    const t2 = setTimeout(() => setPhase(2), 250 + lines.length * 400 + 400);
    return () => [t1, t2].forEach(clearTimeout);
  }, [lines.length]);

  return (
    <section
      className="relative w-full min-h-screen overflow-hidden bg-black flex items-center"
      data-testid="system-manifesto-hero"
    >
      {/* Subtle grain so the black isn't dead-flat */}
      <div className="absolute inset-0 gt-grain opacity-30 pointer-events-none" />

      {/* Cold vertical accent line — acts as a reading rail */}
      <div
        aria-hidden
        className={`absolute left-6 md:left-12 top-[20%] bottom-[20%] w-px bg-white/15 origin-top transition-transform duration-[1400ms] ease-out ${
          phase >= 1 ? "scale-y-100" : "scale-y-0"
        }`}
      />

      <div className="relative z-10 w-full px-6 md:px-20 lg:px-32 py-[180px]">
        <div className="max-w-6xl">
          {sublabel && (
            <div
              className={`wc-label text-[10px] md:text-xs gt-red tracking-[0.4em] mb-14 transition-opacity duration-[900ms] ${
                phase >= 1 ? "opacity-100" : "opacity-0"
              }`}
            >
              {sublabel}
            </div>
          )}

          <div className="space-y-6 md:space-y-10">
            {lines.map((line, i) => (
              <h1
                key={i}
                className={`wc-heading text-white text-3xl md:text-6xl lg:text-7xl leading-[1.05] tracking-widest transition-all duration-[900ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
                  phase >= 1 ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"
                }`}
                style={{ transitionDelay: `${150 + i * 350}ms` }}
                data-testid={`manifesto-line-${i}`}
              >
                {line.split(" ").map((word, wi) => (
                  <span
                    key={wi}
                    className={redWord && word.replace(/[.,]/g, "").toUpperCase() === redWord.toUpperCase() ? "gt-red" : ""}
                  >
                    {word}{" "}
                  </span>
                ))}
              </h1>
            ))}
          </div>

          <div
            className={`mt-16 md:mt-20 flex flex-wrap items-center gap-6 transition-opacity duration-[900ms] ${
              phase >= 2 ? "opacity-100" : "opacity-0"
            }`}
          >
            <StatusBadge status="coming_soon" />
            {ctaHref && (
              <WCButton as="a" href={ctaHref} data-testid="manifesto-hero-cta">
                Join The Waitlist
              </WCButton>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
